import { useEffect } from "react";
import { desktopBridge } from "./desktop";

// Installed web apps (an iPhone home-screen PWA) only get an icon badge from the service worker
// at push time, so a push-set number can outlive the unread chats it counted. The page owns
// the badge while it is open. Electron has its own bridge (useDesktopBadge).
function badgeSupported(): boolean {
  return (
    !desktopBridge() &&
    typeof navigator.setAppBadge === "function" &&
    typeof navigator.clearAppBadge === "function"
  );
}

function applyBadge(count: number) {
  const request = count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge();
  // Badging is a bonus: permission can be denied or revoked without any consequence for the app.
  request.catch(() => undefined);
}

export function useWebAppBadge(count: number) {
  useEffect(() => {
    if (!badgeSupported()) return;
    applyBadge(count);
    // The service worker may have replaced the number while this page was in the background.
    const reapply = () => applyBadge(count);
    const reapplyWhenVisible = () => {
      if (document.visibilityState === "visible") reapply();
    };
    window.addEventListener("focus", reapply);
    document.addEventListener("visibilitychange", reapplyWhenVisible);
    return () => {
      window.removeEventListener("focus", reapply);
      document.removeEventListener("visibilitychange", reapplyWhenVisible);
    };
  }, [count]);
  useEffect(
    () => () => {
      if (badgeSupported()) applyBadge(0);
    },
    [],
  );
}
